// Bucket semântico do Painel K mobile — colapsa prioridade + kanban_status em
// 4 estados de leitura para o operador: aprovada · ruptura · priorizar · saudável.
// Função pura e determinística que reusa a régua canônica do motor
// (isRupturaAcionavel) — não reimplementa nenhuma regra. Usada para a pílula de
// status do card, a cor da barra de progresso e os chips de filtro.

import { isRupturaAcionavel } from './replenishment'
import type { KanbanStatusOp, ReplenishmentPriority } from '../types'

export type PainelBucket = 'aprovada' | 'ruptura' | 'priorizar' | 'saudavel'

export interface BucketInput {
  priority: ReplenishmentPriority
  kanban_status: KanbanStatusOp
  current_stock: number
  em_risco: boolean
}

/**
 * Precedência (a primeira que casar vence):
 * 1. Ruptura acionável → ruptura  (saldo zerado sem motivo operacional; roxo) —
 *    vem ANTES de aprovada: um item zerado continua sem estoque na prateleira
 *    mesmo com a compra já aprovada (o pedido ainda não chegou). É a mesma régua
 *    do Painel/Dashboard desktop (isRupturaAcionavel).
 * 2. Compra Aprovada  → aprovada  (ação tomada e já com estoque; verde)
 * 3. Baixo/Comprar ou em risco → priorizar (precisa decidir compra; laranja)
 * 4. resto (Médio/Alto/SemFornecedor/estados isolados) → saudável (neutro)
 */
export function painelBucket(i: BucketInput): PainelBucket {
  if (isRupturaAcionavel(i.current_stock, i.kanban_status)) return 'ruptura'
  if (i.kanban_status === 'Compra Aprovada') return 'aprovada'
  if (i.priority === 'Baixo' || i.priority === 'Comprar' || i.em_risco) return 'priorizar'
  return 'saudavel'
}

export interface BucketMeta {
  label: string
  /** Cor da bolinha (indicador) da pílula/chip. */
  dot: string
  /** Cor de preenchimento da barra de progresso. */
  bar: string
  /** Classe da pílula de status (fundo + texto, dual-theme). */
  badge: string
}

// Aprovada em VERDE (segue o mockup: sinaliza "resolvido"), priorizar em laranja
// da marca (#FF8A00), ruptura em roxo (#7030A0, mesma cor do Zerado no motor).
export const PAINEL_BUCKET_META: Record<PainelBucket, BucketMeta> = {
  aprovada: {
    label: 'Compra aprovada',
    dot: 'bg-emerald-500',
    bar: 'bg-emerald-500',
    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  },
  ruptura: {
    label: 'Ruptura',
    dot: 'bg-[#7030A0]',
    bar: 'bg-[#7030A0]',
    badge: 'bg-[#7030A0]/12 text-[#7030A0] dark:bg-[#7030A0]/25 dark:text-purple-300',
  },
  priorizar: {
    label: 'Priorizar',
    dot: 'bg-[#FF8A00]',
    bar: 'bg-[#FF8A00]',
    badge: 'bg-[#FF8A00]/15 text-[#b35f00] dark:bg-[#FF8A00]/20 dark:text-amber-300',
  },
  saudavel: {
    label: 'Saudável',
    dot: 'bg-emerald-400',
    bar: 'bg-emerald-400',
    badge: 'bg-sunken text-ink-muted',
  },
}

/** Ordem dos chips de filtro (fora o "Todos", que vem antes). */
export const PAINEL_BUCKET_ORDER: PainelBucket[] = ['ruptura', 'priorizar', 'aprovada', 'saudavel']

export interface PainelKpis {
  /** Nº de itens em ruptura acionável. */
  rupturas: number
  /** Índice de ruptura: rupturas / total de itens, em % (1 casa). Mesma métrica do desktop. */
  rupturaPct: number
  /** Nº de itens que pedem compra (ruptura + priorizar). */
  aRepor: number
  /** Cobertura média em dias (arredondada) dos itens com cobertura finita. */
  coberturaMedia: number
}

/** Meta do índice de ruptura (≤ este valor é saudável). Espelha a meta de compras. */
export const RUPTURA_META_PCT = 7

/** KPIs do topo do Painel K, derivados das mesmas linhas exibidas. */
export function painelKpis(rows: ReadonlyArray<BucketInput & { coverage_days: number }>): PainelKpis {
  let rupturas = 0
  let aRepor = 0
  let covSum = 0
  let covCount = 0
  for (const r of rows) {
    const b = painelBucket(r)
    if (b === 'ruptura') rupturas++
    if (b === 'ruptura' || b === 'priorizar') aRepor++
    if (Number.isFinite(r.coverage_days)) { covSum += r.coverage_days; covCount++ }
  }
  return {
    rupturas,
    rupturaPct: rows.length > 0 ? Math.round((rupturas / rows.length) * 1000) / 10 : 0,
    aRepor,
    coberturaMedia: covCount > 0 ? Math.round(covSum / covCount) : 0,
  }
}

/** Preenchimento da barra (0..100): estoque atual sobre o ponto de reposição. */
export function fillPct(currentStock: number, reorderPoint: number): number {
  if (!(reorderPoint > 0)) return currentStock > 0 ? 100 : 0
  const pct = (currentStock / reorderPoint) * 100
  return Math.max(0, Math.min(100, Math.round(pct)))
}
